/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { IBundle } from '@ahryman40k/ts-fhir-types/lib/R4/Resource';

import { azureDownload, azureListOfFiles } from '../Azure/azure_utils';
import { isProduction } from '../permissions';
import { IPatientAndServer, convertRawDiagnosticReportWithObservationsToSimplified } from '../Fhir/fhir_network';

/** Fetch the list of test patients from azure using await azureListOfFiles() */
export function useTestPatientNameList(includeDevPatients: boolean) {
  const [allPatientNames, setAllPatientNames] = useState<string[] | undefined | null>(undefined); // includes dev patients on production
  const [patientNamesLoading, setPatientNamesLoading] = useState(false);
  const loadPatients = useCallback(async () => {
    setPatientNamesLoading(true);
    setAllPatientNames(undefined);
    try {
      const patients = await azureListOfFiles('.', 'test-patients', 'json');
      // In production, remove all patients starting with "dev/"
      setAllPatientNames(patients);
    } catch (err) {
      console.error(err);
      setAllPatientNames(null);
    } finally {
      setPatientNamesLoading(false);
    }
  }, []);
  useEffect(() => {
    void loadPatients();
  }, [loadPatients]);
  const patientNames = useMemo(() => {
    if (!isProduction || includeDevPatients) return allPatientNames;
    else return allPatientNames?.filter((p) => !p.startsWith('dev/'));
  }, [allPatientNames, includeDevPatients]);
  return { patientNamesLoading, patientNames };
}

export async function fetchPatientDataFromAzure(azureFilename: string) {
  const patientDataAsStr = await azureDownload(azureFilename, 'test-patients');
  const { patientRaw, diagnosticReportsRaw } = JSON.parse(patientDataAsStr);

  if (patientRaw?.resourceType !== 'Patient') {
    throw new Error(`Patient data is not a Patient resource: ${patientRaw}`);
  }
  const patient = { ...patientRaw, server: 'azure' } as IPatientAndServer;
  if (diagnosticReportsRaw?.resourceType !== 'Bundle') {
    throw new Error(`Patient data is not a Bundle resource: ${diagnosticReportsRaw}`);
  }
  const diagnosticReports = convertRawDiagnosticReportWithObservationsToSimplified(diagnosticReportsRaw as IBundle);

  return { patient, diagnosticReports };
}
